/**
 * Store pour les validations des sujets du client connecté
 */

import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import axios from '@js/tools/axios';
/***/
import { useAuthUserStore } from '@stores/auth';

export const useAuthPlanningSubjectsValidationStore = defineStore('authPlanningSubjectsValidation', (() => {

    // Store de l'utilisateur connecté
    const authUserStore = useAuthUserStore();

    // State des validations chargées
    const validations = ref([]);

    /**
     * Retourne une validation par jeton
     * @param string token
     * @return object|null
     */
    const getValidationByToken = computed(() => {
        return ((token) => {
            const index = validations.value.findIndex((validationFromState) => validationFromState.token === token);
            if(index === -1) {
                return null;
            }
            return validations.value[index];
        });
    });

    /**
     * Retourne une validation en fonction de la période en paramètre
     * @param object period
     * @return object|null
     */
    const getValidationByPeriod = computed(() => {
        return ((year, month) => {
            const targetValidation = validations.value.find((validation) => {
                const period = validation.periods.filter((currentPeriod) => currentPeriod.year === year && currentPeriod.month === month).at(0);
                return (period !== undefined);
            });

            return (targetValidation ?? null);
        });
    });

    /**
     * Supprime du store la validation dont le jeton est en paramètre
     * @param string token
     * return object|null
     */
    const removeByToken = ((token) => {
        validations.value = validations.value.filter((currentValidation) => currentValidation.token !== token);
    });

    /**
     * Chargement des données d'une validation par le jeton en paramètre
     * @param string token
     * @param bool refresh Vrai si on force l'appel Ajax
     * @return Promise
     */
    const loadValidationByToken = ((token, refresh = false) => {

        if(! refresh && getValidationByToken.value(token) !== null) {
            return Promise.resolve();
        }


        const path = '/api/me/subjects/validation/:token'.replace(':token', token);

        return axios.get(path).then((response) => {
            if(response && response.data && response.data.success && response.data.validation && response.data.validation.canManage) {
                updateValidation(response.data.validation);
                return Promise.resolve();
            }
            return Promise.reject();
        });

    });

    /**
     * Suppression du sujet en paramètre
     * @param Object validation
     * @param Object subject
     * @return Promise
     */
    const deleteSubject = ((validation, subject) => {

        const path = `/api/me/subjects/validation/${ validation.token }/subjects/${ subject.id }`;
        return axios.delete(path).then((response) => {
            if(response && response.data && response.data.success) {
                return Promise.resolve();
            }
            return Promise.reject();
        });

    });

    /**
     * Régénération du sujet en paramètre
     * @param Object validation
     * @param Object subject
     * @param Object requestParams
     * @return Promise
     */
    const regenerateSubject = ((validation, subject, requestParams) => {

        const path = '/api/me/subjects/validation/:token/subjects/:subjectId/regenerate'
            .replace(':token', validation.token)
            .replace(':subjectId', subject.id)
        ;

        return axios.post(path, requestParams).then((response) => {
            if(response && response.data && response.data.success && response.data.subject) {

                // Mise à jour du store de l'utilisateur connecté
                if(response.data.creditCounters) {
                    authUserStore.updateCurrentUserData({
                        credits: response.data.creditCounters
                    });
                }

                const validationIndex = validations.value.findIndex((validationFromState) => validationFromState.token === validation.token);
                if(validationIndex === -1) {
                    return Promise.reject();
                }
                const currentValidation = validations.value[validationIndex];

                // Mise à jour du sujet dans le store de la validation
                const subjectIndex = currentValidation.subjects.findIndex((currentSubject) => currentSubject.id === subject.id);
                if(subjectIndex === -1) {
                    currentValidation.subjects.push(response.data.subject);
                } else {
                    currentValidation.subjects[subjectIndex] = response.data.subject;
                }

                validations.value[validationIndex].subjects = [ ...currentValidation.subjects ];

                // Retourne le nouveau sujet dans la promesse
                const newSubject = currentValidation.subjects.filter((currentSubject) => currentSubject.id === subject.id).at(0);

                return Promise.resolve(newSubject);
            }
            return Promise.reject();
        }).catch((response) => {
            return Promise.reject(response)
        });

    });

    /**
     * Modifie une validation dans le store
     * @param object validation Données de la validation à mettre à jour dans le store
     * @return Promise
     */
    const updateValidation = ((validation) => {

        const currentValidation = validations.value;

        const index = currentValidation.findIndex((validationFromState) => validationFromState.token === validation.token);
        if(index !== -1) {
            validations.value[index] = { ...validations.value[index], ...validation };
        } else {
            validations.value.push(validation);
        }

    });

    /**
     * Mise à jour des données du sujet en base de données
     * @param object validation
     * @param object subjectData
     * @return Promise
     */
    const updateSubjectData = ((validation, subjectData) => {

        const path = `/api/me/subjects/validation/${ validation.token }/subjects/${ subjectData.id }`

        return axios.post(path, subjectData).then((response) => {
            if (response && response.data && response.data.success && response.data.subject) {
                return Promise.resolve(response.data.subject);
            }
            return Promise.reject();
        });
    });

    /**
     * Ajoute le sujet en base de données
     * @param object validation
     * @param object subjectData
     * @return Promise
     */
    const storeSubjectData = ((validation, subjectData) => {

        const path = `/api/me/subjects/validation/${ validation.token }/subjects`;

        return axios.post(path, subjectData).then((response) => {
            if (response && response.data && response.data.success && response.data.subject) {
                return Promise.resolve(response.data.subject);
            }
            return Promise.reject();
        });

    });

    return {
        getValidationByToken,
        getValidationByPeriod,
        loadValidationByToken,
        updateValidation,
        updateSubjectData,
        storeSubjectData,
        removeByToken,
        regenerateSubject,
        deleteSubject
    };

}));


